// field-svg.js - one source of truth for the KHC brand colours and the
// both-D hockey-pitch markings.
//
// The pitch used to be drawn in two near-identical copies: the line-up field
// (lineup.js) and the DTA field-diagram builder/renderer (dta-field.js). Both
// are the same 100 x 150 (portrait, attacking up) field, so they now share
// `hockeyMarkings()` here. The brand palette was likewise re-declared in
// several SVG modules; `BRAND` is the single definition they import.
//
// No DOM, no network: pure SVG-string output, covered by tests/dta-field.test.js.

// KHC brand colours (formerly re-declared in dta-field / dta-illustrations / lineup).
export const BRAND = {
  navy:      '#1C2A52',
  navyDark:  '#101b38',
  fieldFill: '#16223f',
  gold:      '#A78F58',
  red:       '#B60B2A',
  light:     '#F5F4F0',
};

// Pitch-line inks (white at alpha), shared by the line-up + DTA fields.
export const INK = {
  mark:  'rgba(255,255,255,0.22)',   // solid markings (side/back lines, centre, D)
  faint: 'rgba(255,255,255,0.12)',   // faint markings (23m lines, dashed 5m arc)
  spot:  'rgba(255,255,255,0.4)',    // penalty + centre spots
};

// The full field is a 100 x 150 portrait space; attack is UP.
export const FIELD_W = 100;
export const FIELD_H = 150;

/**
 * Both-D field-hockey markings (two shooting circles + dashed 5m arcs, the two
 * 23m lines, the centre line, and the penalty + centre spots) in the 100 x 150
 * space. Pass `backing: true` to lay a full-bleed navy-dark rectangle behind it
 * (the DTA builder sits on the modal background and wants this; the line-up
 * field sits on its own card and does not).
 */
export function hockeyMarkings({ backing = false } = {}) {
  const R = 24, RO = 29;            // shooting-circle + dashed 5m arc radii
  const q = 0.25 * (FIELD_H - 6);   // 23m line offset
  const back = backing ? `<rect x="0" y="0" width="${FIELD_W}" height="${FIELD_H}" fill="${BRAND.navyDark}"/>\n  ` : '';
  return `${back}<rect x="2" y="3" width="96" height="${FIELD_H - 6}" rx="2" fill="${BRAND.fieldFill}" stroke="${BRAND.gold}" stroke-width="0.7"/>
  <line x1="2" y1="${FIELD_H / 2}" x2="98" y2="${FIELD_H / 2}" stroke="${INK.mark}" stroke-width="0.5"/>
  <line x1="2" y1="${3 + q}" x2="98" y2="${3 + q}" stroke="${INK.faint}" stroke-width="0.5"/>
  <line x1="2" y1="${FIELD_H - 3 - q}" x2="98" y2="${FIELD_H - 3 - q}" stroke="${INK.faint}" stroke-width="0.5"/>
  <path d="M${50 - R} 3 A${R} ${R} 0 0 0 ${50 + R} 3" fill="none" stroke="${INK.mark}" stroke-width="0.6"/>
  <path d="M${50 - RO} 3 A${RO} ${RO} 0 0 0 ${50 + RO} 3" fill="none" stroke="${INK.faint}" stroke-width="0.5" stroke-dasharray="1 2.5"/>
  <path d="M${50 - R} ${FIELD_H - 3} A${R} ${R} 0 0 1 ${50 + R} ${FIELD_H - 3}" fill="none" stroke="${INK.mark}" stroke-width="0.6"/>
  <path d="M${50 - RO} ${FIELD_H - 3} A${RO} ${RO} 0 0 1 ${50 + RO} ${FIELD_H - 3}" fill="none" stroke="${INK.faint}" stroke-width="0.5" stroke-dasharray="1 2.5"/>
  <circle cx="50" cy="${3 + R * 0.52}" r="0.9" fill="${INK.spot}"/>
  <circle cx="50" cy="${FIELD_H - 3 - R * 0.52}" r="0.9" fill="${INK.spot}"/>
  <circle cx="50" cy="${FIELD_H / 2}" r="0.9" fill="${INK.spot}"/>`;
}
